import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format, startOfToday } from "date-fns";
import { Loader2, Send, CalendarDays } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  "Clinician",
  "Researcher",
  "Caregiver",
  "Family Member",
  "Hospital / Health System",
  "Memory Care Center",
  "Senior Living Community",
  "Investor",
  "Other",
];

const INTERESTS = ["Request a Demo", "Become an Early Collaborator", "General Inquiry"];

const TIME_SLOTS = ["Morning (9 AM – 12 PM)", "Afternoon (12 – 4 PM)", "Evening (4 – 7 PM)"];

const inputCls =
  "w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-[#111827] placeholder:text-gray-400 focus:outline-none focus:border-[#7F9F43] focus:ring-2 focus:ring-[#7F9F43]/15 transition-[border-color,box-shadow] duration-300";

const triggerCls =
  "w-full h-auto rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm shadow-none data-[placeholder]:text-gray-400 focus:ring-2 focus:ring-[#7F9F43]/15 focus:border-[#7F9F43] [&>svg]:text-gray-400";

const contentCls = "rounded-xl border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.08)]";

const itemCls =
  "rounded-lg py-2.5 pl-3 text-sm focus:bg-[#7F9F43]/10 focus:text-[#3d4a1e] cursor-pointer";


export default function ContactSection() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    role: "",
    interest: INTERESTS[0],
    preferred_date: "",
    preferred_time: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        preferred_date: form.preferred_date || null,
        preferred_time: form.preferred_time || null,
      });
      toast.success("Thank you — we'll be in touch soon.");
      setForm({ name: "", email: "", organization: "", role: "", interest: INTERESTS[0], preferred_date: "", preferred_time: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-24 lg:py-32" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal>
              <Eyebrow number="11" label="Contact" />
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#111827]">
                Let's Build the Future of{" "}
                <span className="font-serif-italic text-[#7F9F43]">Cognitive Care</span> Together.
              </h2>
              <p className="mt-6 text-gray-500 leading-relaxed text-base md:text-lg max-w-md">
                Whether you are a clinician, researcher, caregiver, hospital, investor, or
                potential collaborator — we would love to connect with you.
              </p>
              <a
                href="mailto:upinderr@huminence.com"
                data-testid="contact-email-link"
                className="mt-8 inline-flex text-sm font-semibold text-[#7F9F43] hover:text-[#6e8b39] transition-colors duration-300"
              >
                upinderr@huminence.com
              </a>
              <div className="mt-10 rounded-3xl overflow-hidden card-soft relative hidden lg:block max-w-md">
                <img
                  src="/assets/romii-detail.jpg"
                  alt="Romii device close-up detail"
                  className="w-full h-56 object-cover hover:scale-[1.03] transition-transform duration-700"
                  data-testid="contact-product-image"
                />
                <p className="absolute bottom-3 left-4 rounded-full backdrop-blur-xl bg-white/75 px-3 py-1.5 text-[10px] tracking-[0.15em] uppercase text-gray-500 font-semibold">
                  Concept Illustration
                </p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.1}>
              <form
                onSubmit={submit}
                className="rounded-3xl bg-[#F9FAFB] border border-gray-100 p-7 lg:p-10 space-y-5"
                data-testid="contact-form"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <input
                    className={inputCls}
                    placeholder="Name *"
                    value={form.name}
                    onChange={set("name")}
                    data-testid="contact-input-name"
                  />
                  <input
                    className={inputCls}
                    type="email"
                    placeholder="Email *"
                    value={form.email}
                    onChange={set("email")}
                    data-testid="contact-input-email"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <input
                    className={inputCls}
                    placeholder="Organization"
                    value={form.organization}
                    onChange={set("organization")}
                    data-testid="contact-input-organization"
                  />
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger className={triggerCls} data-testid="contact-select-role">
                      <SelectValue placeholder="Role" />
                    </SelectTrigger>
                    <SelectContent className={contentCls}>
                      {ROLES.map((r) => (
                        <SelectItem key={r} value={r} className={itemCls}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Select value={form.interest} onValueChange={(v) => setForm({ ...form, interest: v })}>
                  <SelectTrigger className={triggerCls} data-testid="contact-select-interest">
                    <SelectValue placeholder="I'm interested in" />
                  </SelectTrigger>
                  <SelectContent className={contentCls}>
                    {INTERESTS.map((i) => (
                      <SelectItem key={i} value={i} className={itemCls}>{i}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.interest === "Request a Demo" && (
                  <div className="grid sm:grid-cols-2 gap-5" data-testid="contact-schedule-fields">
                    <div>
                      <label className="block text-xs font-semibold tracking-wide uppercase text-gray-400 mb-2">
                        Preferred date
                      </label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            data-testid="contact-input-date"
                            className={`${inputCls} flex items-center justify-between text-left ${form.preferred_date ? "" : "text-gray-400"}`}
                          >
                            {form.preferred_date
                              ? format(new Date(`${form.preferred_date}T00:00:00`), "MMMM d, yyyy")
                              : "Pick a date"}
                            <CalendarDays size={16} className="text-gray-400 shrink-0 ml-2" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className={`${contentCls} w-auto p-0`} align="start">
                          <Calendar
                            mode="single"
                            selected={form.preferred_date ? new Date(`${form.preferred_date}T00:00:00`) : undefined}
                            onSelect={(d) =>
                              setForm({ ...form, preferred_date: d ? format(d, "yyyy-MM-dd") : "" })
                            }
                            disabled={{ before: startOfToday() }}
                            classNames={{
                              day_selected:
                                "bg-[#7F9F43] text-white hover:bg-[#7F9F43] hover:text-white focus:bg-[#7F9F43] focus:text-white rounded-lg",
                              day_today: "bg-[#E89A4D]/15 text-[#c97f35] rounded-lg",
                            }}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold tracking-wide uppercase text-gray-400 mb-2">
                        Preferred time
                      </label>
                      <Select
                        value={form.preferred_time}
                        onValueChange={(v) => setForm({ ...form, preferred_time: v })}
                      >
                        <SelectTrigger className={triggerCls} data-testid="contact-select-time">
                          <SelectValue placeholder="Time slot" />
                        </SelectTrigger>
                        <SelectContent className={contentCls}>
                          {TIME_SLOTS.map((t) => (
                            <SelectItem key={t} value={t} className={itemCls}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
                <textarea
                  className={`${inputCls} min-h-[130px] resize-none`}
                  placeholder="Message *"
                  value={form.message}
                  onChange={set("message")}
                  data-testid="contact-input-message"
                />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-button"
                  className="inline-flex items-center gap-2 rounded-full bg-[#7F9F43] px-8 py-3.5 text-sm font-semibold text-white hover:bg-[#6e8b39] hover:-translate-y-0.5 transition-[background-color,transform] duration-300 disabled:opacity-60"
                >
                  {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
                  {sending ? "Sending..." : "Get in Touch"}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
